import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const LabResultAnalyzerViewResult = ({ result }) => {
  const getRiskLevelColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'critical':
        return 'bg-error text-error-content';
      case 'high':
        return 'bg-error/20 text-error';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-success/20 text-success';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getFormatQualityColor = (quality) => {
    switch(quality?.toLowerCase()) {
      case 'excellent':
        return 'bg-success/20 text-success';
      case 'good':
        return 'bg-success/20 text-success';
      case 'fair':
        return 'bg-warning/20 text-warning';
      case 'poor':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity?.toLowerCase()) {
      case 'critical':
        return <PhosphorIcons.WarningOctagon className="text-error" size={16} />;
      case 'high':
        return <PhosphorIcons.Warning className="text-error" size={16} />;
      case 'medium':
        return <PhosphorIcons.Warning className="text-warning" size={16} />;
      case 'low':
        return <PhosphorIcons.Info className="text-info" size={16} />;
      default:
        return <PhosphorIcons.Question className="text-base-content" size={16} />;
    }
  };

  const analysis = result?.analysis || {};
  const metadata = result?.metadata || {};
  const validation = analysis?.validation || {};
  const resultStatus = analysis?.result_status || {};
  const processingDetails = metadata?.processing_details || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Validation Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.CheckSquare size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Validation Results</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Validation Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${validation?.is_valid ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
                      {validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                  <div className="text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Confidence</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">
                        {Math.round((validation?.confidence || 0) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Format Quality & Details */}
                <div className="p-3 bg-base-200 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Format Quality</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getFormatQualityColor(validation?.format_quality)}`}>
                      {validation?.format_quality || 'Unknown'}
                    </span>
                  </div>
                  
                  {validation?.validation_details && (
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-1">
                        {validation.validation_details.units_present ? 
                          <PhosphorIcons.Check className="text-success" size={14} /> : 
                          <PhosphorIcons.X className="text-error" size={14} />}
                        <span>Units Present</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {validation.validation_details.reference_ranges_present ? 
                          <PhosphorIcons.Check className="text-success" size={14} /> : 
                          <PhosphorIcons.X className="text-error" size={14} />}
                        <span>Reference Ranges</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {validation.validation_details.values_parseable === true ? 
                          <PhosphorIcons.Check className="text-success" size={14} /> : 
                          <PhosphorIcons.X className="text-error" size={14} />}
                        <span>Values Parseable</span>
                      </div>
                    </div>
                  )}

                  {Array.isArray(validation?.validation_details?.format_issues) && 
                   validation.validation_details.format_issues.length > 0 && (
                    <div className="mt-2">
                      <span className="text-sm text-error">Format Issues:</span>
                      <div className="mt-1 space-y-1">
                        {validation.validation_details.format_issues.map((issue, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-error/80">
                            <PhosphorIcons.WarningCircle size={14} />
                            <span className="capitalize">{issue.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Metric Types */}
                {Array.isArray(validation?.metric_type) && validation.metric_type.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Test Categories</span>
                      {validation?.metrics_count && (
                        <span className="text-xs text-base-content/70">
                          {validation.metrics_count} metrics
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {validation.metric_type.map((type, index) => (
                        <span key={index} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs uppercase">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Clinical Analysis Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Stethoscope size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Clinical Analysis</h3>
              </div>

              <div className="space-y-3">
                {/* Results Summary */}
                {resultStatus?.results_summary && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Results Summary</span>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Normal:</span>
                        <span className="text-success">{resultStatus.results_summary.normal_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Abnormal:</span>
                        <span className="text-warning">{resultStatus.results_summary.abnormal_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Critical:</span>
                        <span className="text-error">{resultStatus.results_summary.critical_count || 0}</span>
                      </div>
                      {resultStatus.results_summary.unverifiable_count != null && (
                        <div className="flex justify-between">
                          <span className="text-base-content/70">Unverifiable:</span>
                          <span>{resultStatus.results_summary.unverifiable_count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Abnormal Results */}
                {Array.isArray(resultStatus?.abnormal_results) && 
                 resultStatus.abnormal_results.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Abnormal Results</span>
                    <div className="space-y-2">
                      {resultStatus.abnormal_results.map((result, idx) => (
                        <div key={idx} className="p-2 bg-base-300 rounded">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{result.test}</span>
                            {getSeverityIcon(result.severity)}
                          </div>
                          <div className="text-sm mt-1">
                            <div className="flex justify-between">
                              <span>Value: {result.value} {result.units}</span>
                              <span className="text-base-content/70">
                                Range: {result.reference_range}
                              </span>
                            </div>
                            <div className="text-base-content/70 mt-1">
                              {result.interpretation}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clinical Implications */}
                {Array.isArray(resultStatus?.clinical_implications) && 
                 resultStatus.clinical_implications.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Clinical Implications</span>
                    <div className="space-y-1">
                      {resultStatus.clinical_implications.map((implication, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <PhosphorIcons.ArrowRight className="text-primary" size={16} />
                          <span className="capitalize">{implication.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending */}
                {resultStatus?.trending && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Trend Analysis</span>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base-content/70">Direction:</span>
                        <span className="capitalize">{resultStatus.trending.direction}</span>
                      </div>
                      {resultStatus.trending.significance && (
                        <div className="text-base-content/70">
                          {resultStatus.trending.significance}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Analysis Metadata */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Analysis Details</span>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Confidence Score</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round((metadata?.confidence_score || 0) * 100)}%
                      </span>
                    </div>
                    {metadata?.panel_type && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Panel Type</span>
                        <span className="capitalize">{metadata.panel_type}</span>
                      </div>
                    )}
                    {processingDetails?.validation_level && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Validation Level</span>
                        <span className="capitalize">{processingDetails.validation_level}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-base-content/70">Analysis Date</span>
                      <span>{new Date(metadata?.analysis_timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabResultAnalyzerViewResult;